// src/pages/landing/TemplatesPage.tsx
// /landing/templates — template gallery. ListLandingTemplates, GetLandingTemplate
// (detail rail), InstallLandingTemplate (creates a page from it).

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useI18n } from '@react/hooks';
import { Button, Badge, Alert, Input } from '@react/components';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { landingApi } from '@core/api';
import type { LandingTemplate } from '@core/types/landing';
import { errMessage, listParams } from '@/features/landing';

export function TemplatesPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<LandingTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [category, setCategory] = useState('');
  const [publicOnly, setPublicOnly] = useState(false);
  const [selected, setSelected] = useState<LandingTemplate | null>(null);
  const [installing, setInstalling] = useState(false);

  const load = () => {
    setLoading(true);
    setError(null);
    landingApi
      .ListLandingTemplates(listParams({ limit: 50 }, { ...(category ? { category } : {}), ...(publicOnly ? { public_only: true } : {}) }))
      .then((res) => setTemplates(res.items?.items ?? []))
      .catch((e: unknown) => setError(errMessage(e, t('landing.templates.loadError'))))
      .finally(() => setLoading(false));
  };

  useEffect(load, [category, publicOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  const openDetail = (id?: string) => {
    if (!id) return;
    landingApi
      .GetLandingTemplate(id)
      .then((res) => setSelected(res.template ?? null))
      .catch((e: unknown) => setError(errMessage(e, t('landing.templates.loadError'))));
  };

  const install = async () => {
    if (!selected?.entity?.id) return;
    setInstalling(true);
    setError(null);
    try {
      const res = await landingApi.InstallLandingTemplate(selected.entity.id, {});
      if (res.page?.entity?.id) navigate(`/landing/${res.page.entity.id}`);
    } catch (e) {
      setError(errMessage(e, t('landing.templates.installError')));
    } finally {
      setInstalling(false);
    }
  };

  return (
    <div className="flex h-full">
      <div className="min-w-0 flex-1 space-y-4 overflow-y-auto p-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h1 className="text-xl font-semibold text-foreground">{t('landing.templates.title')}</h1>
            <p className="text-sm text-muted-foreground">{t('landing.templates.subtitle')}</p>
          </div>
          <Link to="/landing" className="text-sm text-primary hover:underline">
            {t('landing.backToPages')}
          </Link>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder={t('landing.templates.categoryPlaceholder')} className="max-w-xs" />
          <ToggleSwitch label={t('landing.templates.publicOnly')} checked={publicOnly} onChange={setPublicOnly} />
        </div>

        {error && <Alert variant="error">{error}</Alert>}

        {loading && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" aria-busy="true">
            <div className="h-32 animate-pulse rounded-md bg-muted" />
            <div className="h-32 animate-pulse rounded-md bg-muted" />
          </div>
        )}

        {!loading && templates.length === 0 && !error && (
          <div className="rounded-md border border-dashed border-border p-10 text-center">
            <p className="text-sm text-muted-foreground">{t('landing.templates.empty')}</p>
          </div>
        )}

        {!loading && templates.length > 0 && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {templates.map((tmpl) => (
              <button
                key={tmpl.entity?.id}
                onClick={() => openDetail(tmpl.entity?.id)}
                className={`flex flex-col rounded-md border p-4 text-left hover:border-primary ${selected?.entity?.id === tmpl.entity?.id ? 'border-primary' : 'border-border'}`}
              >
                {tmpl.preview_image_url ? (
                  <img src={tmpl.preview_image_url} alt={tmpl.name} className="mb-2 h-24 w-full rounded-md object-cover" />
                ) : (
                  <div className="mb-2 h-24 w-full rounded-md bg-muted" />
                )}
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-foreground">{tmpl.name}</span>
                  {tmpl.is_public && <Badge variant="primary">{t('landing.templates.public')}</Badge>}
                </div>
                {tmpl.category && <p className="mt-1 text-xs text-muted-foreground">{tmpl.category}</p>}
                <p className="mt-1 text-xs text-muted-foreground">{t('landing.templates.installCount', { count: tmpl.install_count })}</p>
              </button>
            ))}
          </div>
        )}
      </div>

      {selected && (
        <aside className="w-80 flex-shrink-0 space-y-3 border-l border-border p-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-foreground">{selected.name}</h3>
            <button onClick={() => setSelected(null)} aria-label={t('common.close')} className="rounded-md p-1 text-muted-foreground hover:bg-muted">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
          </div>
          {selected.description && <p className="text-sm text-muted-foreground">{selected.description}</p>}
          <Button size="sm" className="w-full" onClick={install} disabled={installing}>
            {installing ? t('common.saving') : t('landing.templates.install')}
          </Button>
        </aside>
      )}
    </div>
  );
}
